import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  ExternalLink, 
  Building, 
  Globe, 
  DollarSign, 
  Tag, 
  Share2, 
  Heart, 
  MessageCircle,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  Trash2,
  Plus
} from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { useSystemPreset } from '../../../../contexts/SystemPresetContext';
import { CommunityEventsService } from '../../../../services/communityEvents/CommunityEventsService';
import type { CommunityEvent, EventAgendaItem, EventComment, CreateAgendaItemData } from '../../../../types/communityEvents';

interface EventDetailsPanelProps {
  event: CommunityEvent;
  onEdit?: (event: CommunityEvent) => void;
  onDelete?: (eventId: string) => void;
}

const EventDetailsPanel: React.FC<EventDetailsPanelProps> = ({ 
  event, 
  onEdit, 
  onDelete 
}) => {
  const { language, isRTL } = useLocalization();
  const { presetData } = useSystemPreset();
  
  const [agendaItems, setAgendaItems] = useState<EventAgendaItem[]>([]);
  const [comments, setComments] = useState<EventComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [rsvpStatus, setRsvpStatus] = useState<'attending' | 'not_attending' | 'maybe' | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    loadEventDetails();
  }, [event.id]);

  const loadEventDetails = async () => {
    try {
      setLoading(true);
      const [agendaData, commentsData] = await Promise.all([
        CommunityEventsService.getEventAgenda(event.id),
        CommunityEventsService.getEventComments(event.id)
      ]);
      setAgendaItems(agendaData);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading event details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTime = (dateString: string) => {
    return `${formatDate(dateString)} ${formatTime(dateString)}`;
  };

  const getEventType = () => {
    if (!presetData?.eventTypes) return null;
    return presetData.eventTypes.find(type => type.id === event.communityEventTypeId);
  };

  const getEventStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'completed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const handleRSVP = async (status: 'attending' | 'not_attending' | 'maybe') => {
    try {
      if (rsvpStatus === status) {
        // Cancel RSVP if clicking the same status
        await CommunityEventsService.cancelRSVP(event.id, 'current-user-id'); // TODO: Get actual user ID
        setRsvpStatus(null);
      } else {
        await CommunityEventsService.rsvpToEvent(event.id, 'current-user-id', status); // TODO: Get actual user ID
        setRsvpStatus(status);
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
    }
  };

  const handleLike = async () => {
    try {
      if (isLiked) {
        // Unlike logic here
        setIsLiked(false);
      } else {
        await CommunityEventsService.likeEvent(event.id, 'current-user-id'); // TODO: Get actual user ID
        setIsLiked(true);
      }
    } catch (error) {
      console.error('Error liking event:', error);
    }
  };

  const handleShare = async () => {
    try {
      await CommunityEventsService.shareEvent(event.id, 'current-user-id'); // TODO: Get actual user ID
      // Show share options or copy link
      if (navigator.share) {
        await navigator.share({
          title: event.title,
          text: event.description,
          url: window.location.href
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        alert(language === 'ar' ? 'تم نسخ الرابط' : 'Link copied to clipboard');
      }
    } catch (error) {
      console.error('Error sharing event:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setSubmittingComment(true);
      await CommunityEventsService.addEventComment(
        event.id, 
        newComment, 
        'current-user-id', // TODO: Get actual user ID
        'Current User' // TODO: Get actual user name
      );
      setNewComment('');
      // Reload comments
      const commentsData = await CommunityEventsService.getEventComments(event.id);
      setComments(commentsData);
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const isEventUpcoming = new Date(event.startDate) > new Date();
  const isEventToday = new Date(event.startDate).toDateString() === new Date().toDateString();
  const eventType = getEventType();

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">

        {/* Event Image */}
        {event.imageURL && (
          <div className="mb-6">
            <img 
              src={event.imageURL} 
              alt={event.title}
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        )}

        {/* Title and Status */}
        <div className={`flex items-start justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
              {event.title}
            </h1>
            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed">
              {event.description}
            </p>
          </div>
          <div className={`flex items-center gap-2 ${isRTL ? 'ml-4' : 'mr-4'}`}>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getEventStatusColor(event.status)}`}>
              {language === 'ar' ? 
                (event.status === 'published' ? 'منشور' : 
                 event.status === 'cancelled' ? 'ملغي' : 
                 event.status === 'completed' ? 'مكتمل' : 'مسودة') :
                event.status.charAt(0).toUpperCase() + event.status.slice(1)
              }
            </span>
          </div>
        </div>

        {/* Event Type */}
        {eventType && (
          <div className="mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              {language === 'ar' ? eventType.name_ar : eventType.name_en}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Event Details Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Main Details Cards */}
          <div className="lg:col-span-2 space-y-6">
            {/* Date & Time Card */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'التاريخ والوقت' : 'Date & Time'}
                </h3>
              </div>
              <div className="space-y-3">
                <div className={`flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Clock className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'ar' ? 'البداية' : 'Start Time'}
                    </div>
                    <div className="font-medium text-slate-800 dark:text-white">
                      {formatDateTime(event.startDate)}
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Clock className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'ar' ? 'النهاية' : 'End Time'}
                    </div>
                    <div className="font-medium text-slate-800 dark:text-white">
                      {formatDateTime(event.endDate)}
                    </div>
                  </div>
                </div>
                {event.registrationDeadline && (
                  <div className={`flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <div>
                      <div className="text-sm text-amber-600 dark:text-amber-400">
                        {language === 'ar' ? 'آخر موعد للتسجيل' : 'Registration Deadline'}
                      </div>
                      <div className="font-medium text-slate-800 dark:text-white">
                        {formatDateTime(event.registrationDeadline)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Location Card */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  {event.isOnline ? (
                    <Globe className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <MapPin className="w-5 h-5 text-green-600 dark:text-green-400" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'الموقع' : 'Location'}
                </h3>
              </div>
              <div className="space-y-3">
                {event.isOnline ? (
                  <div className={`flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <ExternalLink className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <div>
                      <div className="text-sm text-green-600 dark:text-green-400">
                        {language === 'ar' ? 'فعالية أونلاين' : 'Online Event'}
                      </div>
                      <div className="font-medium text-slate-800 dark:text-white">
                        {event.meetingLink && (
                          <a 
                            href={event.meetingLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {language === 'ar' ? 'انقر للانضمام' : 'Click to join'}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {event.venueName && (
                      <div className={`flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Building className="w-4 h-4 text-slate-500" />
                        <div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {language === 'ar' ? 'المكان' : 'Venue'}
                          </div>
                          <div className="font-medium text-slate-800 dark:text-white">
                            {event.venueName}
                          </div>
                        </div>
                      </div>
                    )}
                    {event.address && (
                      <div className={`flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <MapPin className="w-4 h-4 text-slate-500" />
                        <div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {language === 'ar' ? 'العنوان' : 'Address'}
                          </div>
                          <div className="font-medium text-slate-800 dark:text-white">
                            {event.address}
                            {event.city && <span>, {event.city}</span>}
                            {event.country && <span>, {event.country}</span>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Requirements Card */}
            {event.requirements && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    {language === 'ar' ? 'متطلبات المشاركة' : 'Participation Requirements'}
                  </h3>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                    {event.requirements}
                  </p>
                </div>
              </div>
            )}

            {/* Tags Card */}
            {event.tags.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                    <Tag className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    {language === 'ar' ? 'العلامات' : 'Tags'}
                  </h3>
                </div>
                <div className={`flex flex-wrap gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  {event.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 text-sm bg-primary/10 text-primary border border-primary/20 rounded-full hover:bg-primary/20 transition-colors"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Cards */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'التسعير' : 'Pricing'}
                </h3>
              </div>
              <div className="text-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                {event.isFree ? (
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
                    {language === 'ar' ? 'مجاني' : 'Free'}
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-slate-800 dark:text-white mb-1">
                    {event.price} {event.currency}
                  </div>
                )}
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'ar' ? 'للمشاركة' : 'per person'}
                </div>
              </div>
            </div>

            {/* Attendees Card */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'المشاركون' : 'Attendees'}
                </h3>
              </div>
              <div className="text-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div className="text-3xl font-bold text-slate-800 dark:text-white mb-1">
                  {event.currentAttendees}
                  {event.maxAttendees && ` / ${event.maxAttendees}`}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'ar' ? 'مشارك' : 'attendees'}
                </div>
                {event.maxAttendees && (
                  <div className="mt-2 w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(event.currentAttendees / event.maxAttendees) * 100}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>

            {/* Organizer Card */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'المنظم' : 'Organizer'}
                </h3>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                {event.organizerPhotoURL ? (
                  <img 
                    src={event.organizerPhotoURL} 
                    alt={event.organizerName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-medium text-slate-800 dark:text-white">
                    {event.organizerName}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {language === 'ar' ? 'منظم الفعالية' : 'Event Organizer'}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Card */}
            {isEventUpcoming && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    {language === 'ar' ? 'الإجراءات' : 'Actions'}
                  </h3>
                </div>
                <div className="space-y-3">
                  {event.isRegistrationRequired && (
                    <div className="space-y-2">
                      <button
                        onClick={() => handleRSVP('attending')}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                          rsvpStatus === 'attending'
                            ? 'bg-green-600 text-white'
                            : 'bg-primary text-white hover:bg-primary-dark'
                        }`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        {language === 'ar' ? 'سأحضر' : 'I will attend'}
                      </button>
                      <button
                        onClick={() => handleRSVP('maybe')}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                          rsvpStatus === 'maybe'
                            ? 'bg-yellow-600 text-white'
                            : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-500'
                        }`}
                      >
                        <AlertCircle className="w-4 h-4" />
                        {language === 'ar' ? 'ربما' : 'Maybe'}
                      </button>
                      <button
                        onClick={() => handleRSVP('not_attending')}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                          rsvpStatus === 'not_attending'
                            ? 'bg-red-600 text-white'
                            : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-500'
                        }`}
                      >
                        <XCircle className="w-4 h-4" />
                        {language === 'ar' ? 'لن أحضر' : 'Won\'t attend'}
                      </button>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleLike}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                        isLiked
                          ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                          : 'bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-500'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                      {event.likeCount}
                    </button>
                    <button
                      onClick={handleShare}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-500 transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                      {language === 'ar' ? 'مشاركة' : 'Share'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Agenda */}
        {agendaItems.length > 0 && (
          <div className="mb-8">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                {language === 'ar' ? 'جدول الأعمال' : 'Event Agenda'}
              </h3>
              <div className="space-y-4">
                {agendaItems.map((item, index) => (
                  <div key={item.id} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                    <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-sm font-semibold text-primary">
                          {index + 1}
                        </span>
                      </div>
                      <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <h4 className="font-semibold text-slate-800 dark:text-white mb-2">
                          {item.title}
                        </h4>
                        {item.description && (
                          <p className="text-slate-600 dark:text-slate-300 mb-3">
                            {item.description}
                          </p>
                        )}
                        <div className={`flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="flex items-center gap-2 px-2 py-1 bg-white dark:bg-slate-800 rounded-md">
                            <Clock className="w-4 h-4" />
                            {item.startTime} - {item.endTime}
                          </span>
                          {item.speakerName && (
                            <span className="flex items-center gap-2 px-2 py-1 bg-white dark:bg-slate-800 rounded-md">
                              <User className="w-4 h-4" />
                              {item.speakerName}
                              {item.speakerTitle && ` (${item.speakerTitle})`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-slate-800 dark:text-white flex items-center gap-3">
              <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-pink-600 dark:text-pink-400" />
              </div>
              {language === 'ar' ? 'التعليقات' : 'Comments'} ({comments.length})
            </h3>
            <button
              onClick={() => setShowComments(!showComments)}
              className="px-4 py-2 text-primary hover:text-primary-dark hover:bg-primary/10 rounded-lg transition-colors"
            >
              {showComments ? 
                (language === 'ar' ? 'إخفاء التعليقات' : 'Hide Comments') :
                (language === 'ar' ? 'عرض التعليقات' : 'Show Comments')
              }
            </button>
          </div>

          {showComments && (
            <div className="space-y-6">
              {/* Add Comment Card */}
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                <div className="flex gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={language === 'ar' ? 'اكتب تعليقك هنا...' : 'Write your comment here...'}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white resize-none"
                      rows={3}
                    />
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || submittingComment}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {submittingComment ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        {language === 'ar' ? 'إضافة تعليق' : 'Add Comment'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments List */}
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                    <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-slate-800 dark:text-white">
                            {comment.authorName}
                          </span>
                          <span className="text-sm text-slate-500 dark:text-slate-400">
                            {formatDate(comment.createdAt)}
                          </span>
                        </div>
                        <p className="text-slate-600 dark:text-slate-300">
                          {comment.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {(onEdit || onDelete) && (
          <div className="border-t border-slate-200 dark:border-slate-700 p-6">
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {onEdit && (
                <button
                  onClick={() => onEdit(event)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
                >
                  <Edit className="w-4 h-4" />
                  {language === 'ar' ? 'تعديل الفعالية' : 'Edit Event'}
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(event.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  {language === 'ar' ? 'حذف الفعالية' : 'Delete Event'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailsPanel;
